import Payment from 'payment'
import CardExpiryService from '../payment/CardExpiryService';
import MvcField from './MvcField';
import MvcValidationService from './MvcValidationService';
import PaymentField from './PaymentField';
import PaymentFieldType from './PaymentFieldType';
import SpreedlyException from './SpreedlyException';
import SpreedlyField from './SpreedlyField';
import SpreedlyService from './SpreedlyService';
import SpreedlyValidationException from './SpreedlyValidationException';
import ValidationResult from './ValidationResult';

export default class SpreedlyPayment {
    private readonly spreedlyFields: SpreedlyField[];
    private readonly mvcFields: MvcField[];
    private readonly allSortedFields: PaymentField[];
    private readonly paymentMethodField: JQuery<HTMLInputElement>;
    private isFormSubmitted = false;
    private cardExpiryService: CardExpiryService;

    constructor(
        private readonly paymentForm: JQuery<HTMLFormElement>,
        private readonly submitButton: JQuery<HTMLButtonElement>,
        private readonly spreedlyService: SpreedlyService,
        private readonly mvcValidationService: MvcValidationService,
    ) {
        const defaultInputStyle = {
            'display': 'block',
            'width': '100%',
            'height': '100%',
            'margin': '0',
            'padding': '13px',
            'font-size': '16px',
            'font-family': 'Roboto, sans - serif',
            'box-sizing': 'border-box',
            'position': 'absolute',
            'top': '0',
            'left': '0',
            'right': '0',
            'bottom': '0',
        };

        this.spreedlyService.initialize(defaultInputStyle, defaultInputStyle);
        const nameField = new MvcField(
            PaymentFieldType.cardholdername,
            'CardHolderName'
        );
        const expiryField = new MvcField(
            PaymentFieldType.cardexpiry,
            'CardExpiry'
        );
        const countryField = new MvcField(
            PaymentFieldType.cardholdercountry,
            'CardHolderCountry'
        );
        const addressField = new MvcField(
            PaymentFieldType.cardholderaddress,
            'CardHolderAddress'
        );
        const cityField = new MvcField(
            PaymentFieldType.cardholdercity,
            'CardHolderCity'
        );
        const postalCodeField = new MvcField(
            PaymentFieldType.cardholderpostalcode,
            'CardHolderPostalCode'
        );
        const isThreeDsEnabled = document.querySelector(`.card-holder-address-form`) !== null;

        const numberField = new SpreedlyField(
            PaymentFieldType.number,
            this.spreedlyService,
        );
        const cvvField = new SpreedlyField(
            PaymentFieldType.cvv,
            this.spreedlyService,
        );
        this.spreedlyFields = [numberField, cvvField];
        this.mvcFields = [nameField, expiryField];

        this.allSortedFields = [
            numberField,
            nameField,
            expiryField,
            cvvField,
        ];

        if (isThreeDsEnabled) {
            this.mvcFields.push(countryField, addressField, cityField, postalCodeField);

            this.allSortedFields.push(countryField, addressField, cityField, postalCodeField);
        }

        this.paymentMethodField = this.paymentForm.find(
            'input[name="paymentMethod"]',
        ) as any;
        this.formatCreditExpiry();
        // Handle Spreedly events
        this.handleIFrameFieldsEvent();
        this.handlePaymentTypeChange();
        this.cardExpiryService = new CardExpiryService();
    }

    public async submitForm(event: JQuery.SubmitEvent): Promise<void> {
        this.isFormSubmitted = true;

        // If we have tokenizeResult, just continue a normal form submit to a server.
        if (this.tokenizeResult) {
            return;
        }

        // Validating form input and request a token if no invalid input
        event.preventDefault();
        const spreedlyValidationResult = await this.spreedlyService.validate();
        const mvcValidationResult = this.mvcValidationService.validate();
        const allValidationResult = spreedlyValidationResult.concat(mvcValidationResult);

        if (allValidationResult.length) {
            this.renderErrorMessage(allValidationResult);
            this.enableSubmitButton = true;
            return;
        }

        try {
            this.enableSubmitButton = false;
            const paymentData = await this.spreedlyService.requestToken(
                this.createCreditCardTokenRequest(),
                this.getSelectedPaymentType(),
            );

            this.tokenizeResult = paymentData;
            this.spreedlyService.setBrowserInfo();
            this.paymentForm.trigger('submit');
        } catch (error: any) {
            if (error.paymentData) {
                const exception = error as SpreedlyException;
                this.tokenizeResult = exception.paymentData;
                this.paymentForm.trigger('submit');
                return;
            }

            // For error that not trigger form submit, just enable book now button again.
            this.enableSubmitButton = true;
            // Under jest test, we can't use instance of to check type,
            // so we need to check if error has property
            // https://github.com/facebook/jest/issues/6788
            // https://github.com/facebook/jest/issues/2549
            if (error.validationResult) {
                const exception = error as SpreedlyValidationException;
                this.renderErrorMessage(exception.validationResult);
                return;
            }

            // We want to log exception that we can't handle.
            // tslint:disable-next-line:no-console
            console.error(error);
        }
    }

    private renderErrorMessage(validationResult: ValidationResult[]): void {
        this.spreedlyFields.forEach(field => field.setDirtyState(this.isFormSubmitted));
        this.mvcFields.forEach(field => field.setDirtyState(this.isFormSubmitted));
        this.updateFieldsErrorMessage(validationResult, this.allSortedFields);
        this.allSortedFields.forEach(field => field.renderErrorMessage());
        this.focusToTheTopInvalidField();
    }

    private set enableSubmitButton(isEnable: boolean) {
        this.submitButton.prop('disabled', !isEnable);
    }

    private get tokenizeResult(): string {
        return this.getTokenInput().val() as string;
    }

    private set tokenizeResult(value: string) {
        this.getTokenInput().val(value);
    }

    private getTokenInput() {
        return this.paymentForm.find('input[name="paymentResult"]');
    }

    private createCreditCardTokenRequest(): ICreditCardTokenRequest {
        const fullNameInput = this.paymentForm.find('#CardHolderName');
        const fullName = fullNameInput.val() as string;

        const cardExpiryValue = this.getCardExpiryField().val() as string;
        this.cardExpiryService.createCardExpiry(cardExpiryValue);
        if (!this.cardExpiryService.isValid) {
            throw new Error(this.cardExpiryService.errorMessage);
        }

        const country = this.paymentForm.find('#CardHolderCountry').val() as string;
        const address = this.paymentForm.find('#CardHolderAddress').val() as string;
        const city = this.paymentForm.find('#CardHolderCity').val() as string;
        const postalCode = this.paymentForm.find('#CardHolderPostalCode').val() as string;

        return {
            full_name: fullName,
            month: this.cardExpiryService.month,
            year: this.cardExpiryService.year,
            address1: address,
            city: city,
            country: country,
            zip: postalCode
        };
    }

    private getCardExpiryField(): JQuery {
        return this.paymentForm.find('#CardExpiry');
    }

    private getSelectedPaymentType(): string {
        const selectedPaymentType = this.paymentMethodField.val();
        switch (selectedPaymentType) {
            case 'VSA':
                return 'visa';
            case 'VSADebit':
                return 'visa';
            case 'MC':
                return 'master';
            case 'MCDebit':
                return 'master';
            case 'AMEX':
                return 'american_express';
            default:
                return '';
        }
    }

    private getMatchPaymentMethodField(spreedlyPaymentType): string {
        switch (spreedlyPaymentType) {
            case 'visa':
                return 'VSA';
            case 'visa':
                return 'VSADebit';
            case 'master':
                return 'MC';
            case 'master':
                return 'MCDebit';
            case 'american_express':
                return 'AMEX';
            default:
                return '';
        }
    }

    private formatCreditExpiry(): void {
        const id = this.getCardExpiryField().attr('id');
        if (id) {
            Payment.formatCardExpiry(document.getElementById(id));
        }
        else {
            throw "missing card expiry";
        }
    }

    private handleIFrameFieldsEvent(): void {
        this.spreedlyService.handleIFrameFieldsEvent(
            (currentFieldType, validationResult, inputProperties) => {
                const currentField = this.spreedlyFields.find(
                    field => field.type === currentFieldType,
                ) as SpreedlyField;

                currentField.valueLength =
                    currentField.type === PaymentFieldType.number
                        ? inputProperties.numberLength
                        : inputProperties.cvvLength;

                this.updateFieldsErrorMessage(validationResult, this.spreedlyFields);
                currentField.renderErrorMessage();
                if (currentField.type === PaymentFieldType.number && inputProperties.cardType) {
                    if ($('#paymentMethod').val() !== this.getMatchPaymentMethodField(inputProperties.cardType)) {
                        $('#paymentMethod').val(this.getMatchPaymentMethodField(inputProperties.cardType)).trigger('change');
                    }
                }
            },
            currentFieldType => {
                const currentField = this.spreedlyFields.find(
                    field => field.type === currentFieldType,
                ) as SpreedlyField;
                currentField.setDirtyState(this.isFormSubmitted);
                // This improves UX when a customer correct credit card number
                // and also remove an error message on the correct CVV.
                // If a customer enter an invalid credit card number, Spreedly treat CVV as an invalid value
                // even though a customer fill a correct CVV.
                this.spreedlyFields.forEach(field =>
                    field.renderErrorMessage(),
                );
            });

        this.mvcValidationService.handleIFrameFieldsEvent(this.mvcFields);
    }

    private updateFieldsErrorMessage(validationResults: ValidationResult[], paymentFields: PaymentField[]) {
        paymentFields.forEach(field => {
            const validationResult = validationResults.find(
                result => result.PaymentFieldType === field.type,
            );
            field.validationErrorMessage = validationResult ? validationResult.ErrorMessage : '';
        });
    }

    private focusToTheTopInvalidField() {
        const invalidFields = this.allSortedFields.filter(
            f => f.validationErrorMessage,
        );
        invalidFields[0].focus();
    }

    private handlePaymentTypeChange() {
        this.paymentMethodField.on('change', () => {
            const numberField = this.spreedlyFields.find(
                field => field.type === PaymentFieldType.number,
            ) as SpreedlyField;
            numberField.validationErrorMessage = '';
            numberField.renderErrorMessage();
            const cardType = this.paymentMethodField.val();
            $('.payment-method-group .icon-payment').attr('class', `icon-payment icon-${cardType ? cardType : 'default'}`);
        });
    }
}
